import { useEffect } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { describeAppLink, extractInAppPath } from '@/lib/deepLink';
import { navigateTo } from '@/lib/navigationRef';
import { logAction } from '@/services/eventLogService';
import { supabase } from '@/integrations/supabase/client';

const openByLink = async (path: string, launch: 'warm' | 'cold') => {
  logAction('navigation', 'app_link_opened', { ...describeAppLink(path), launch });
  // The plan email links into the funnel. A member who is already signed in
  // (paid, then tapped the email later) must not land on a public paywall
  // that asks them to pay again: send them home instead.
  if (path === '/start' || path.startsWith('/start/')) {
    try {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        navigateTo('/');
        return;
      }
    } catch {
      /* no session read: fall through to the funnel */
    }
  }
  navigateTo(path);
};

/**
 * Routes email / notification Universal Links (iOS) & App Links (Android) into
 * the app. When a user taps the "פתח באפליקציה" button in an email, the OS opens
 * the installed app (rather than the browser) and we navigate to the matching
 * in-app screen.
 *
 * Native-only: on web the browser loads the URL directly, and the
 * open.stayyoung.live → stayyoung.lovable.app 302 fallback already lands the
 * user on the right page. See src/lib/deepLink.ts and the applinks-host project.
 *
 * Requires a native release that carries the associated-domains entitlement /
 * App Links intent-filter (added 2026-06, first shipped in 1.2.3) — Universal
 * Links are verified against the installed binary at install time.
 *
 * Every link the OS hands us is logged as `app_link_opened`. It is the only
 * evidence that Universal / App Links work at all: a link that falls through to
 * the browser never reaches this hook, so a section that shows web visits and
 * no `app_link_opened` means the AASA or the intent-filter is not claiming it.
 */
export const useDeepLinks = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Warm open: the app is already running and the OS hands us the tapped link.
    const listenerPromise = CapApp.addListener('appUrlOpen', ({ url }) => {
      const path = extractInAppPath(url);
      if (path) openByLink(path, 'warm');
    });

    // Cold open: the app was launched by the link. getLaunchUrl returns the
    // entry URL (undefined / bare host on a normal icon-tap launch — skip "/").
    CapApp.getLaunchUrl()
      .then((res) => {
        const path = extractInAppPath(res?.url);
        if (path && path !== '/') openByLink(path, 'cold');
      })
      .catch(() => {});

    return () => {
      listenerPromise.then((listener) => listener.remove()).catch(() => {});
    };
  }, []);
};
